import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight, Sparkles, BookOpen } from 'lucide-react';
import { SectionTitle, PremiumButton, FlowerMotif, GoldDivider, OmMark } from './premium/Decor';

const POINTS = [
  {
    hi: 'आपके जन्म समय पर ग्रहों की सटीक स्थिति को प्रकट करता है।',
    en: 'Reveals the precise planetary positions at the time of your birth.',
  },
  {
    hi: 'करियर, धन, रिश्ते, स्वास्थ्य और जीवन की प्रमुख घटनाओं की अंतर्दृष्टि।',
    en: 'Uncovers insights about your career, finances, relationships, health, and major life events.',
  },
  {
    hi: 'आपके व्यक्तित्व, जीवन पथ और भविष्य की संभावनाओं को उजागर करता है।',
    en: 'Reveals insights about your personality, life path, and future possibilities.',
  },
  {
    hi: 'शक्तिशाली वैदिक ज्योतिष विधियों के माध्यम से सर्वोत्तम अवसरों को खोलता है।',
    en: 'Unlocks best opportunities through powerful Vedic astrology methods.',
  },
];

const WhatIsKundliSection = ({ onOpenForm }) => {
  const { language } = useLanguage();
  const isHi = language === 'hi';

  return (
    <section
      data-testid="what-is-kundli-section"
      className="relative bg-white py-16 md:py-24 overflow-hidden"
    >
      <div className="absolute -top-40 -right-32 w-[500px] h-[500px] bg-mandala-large bg-no-repeat bg-center opacity-25 pointer-events-none" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* LEFT: Copy */}
          <div className="order-2 lg:order-1">
            <p className="text-brand-gold-dark uppercase tracking-[0.25em] text-xs sm:text-sm font-semibold mb-3">
              {isHi ? 'परिचय' : 'Introduction'}
            </p>
            <h2 className={`font-playfair font-bold text-3xl md:text-4xl lg:text-5xl text-brand-maroon leading-tight mb-3 ${isHi ? 'font-hindi-serif' : ''}`}>
              {isHi
                ? 'प्रीमियम पर्सनलाइज़्ड कुंडली रिपोर्ट क्या है?'
                : 'What Is Premium Personalized Kundli Report?'}
            </h2>
            <GoldDivider className="!my-4 lg:!justify-start" />
            <p className={`text-brand-text-soft text-base md:text-lg leading-relaxed mb-7 ${isHi ? 'font-hindi' : 'font-outfit'}`}>
              {isHi
                ? 'यह आपकी जन्म कुंडली को समझने का संपूर्ण मार्गदर्शन है। कर्मा कुंडली आपकी जन्म कुंडली में छिपी गहरी ज्योतिषीय अंतर्दृष्टि को प्रकट करती है ताकि आप अपनी ताकत, चुनौतियों और आने वाले अवसरों को समझ सकें।'
                : 'It is your complete guide to understanding your birth chart. Karma Kundali reveals the deeper astrological insights within your Janam Kundli to help you understand your strengths, challenges, and upcoming opportunities.'}
            </p>

            <ul className="space-y-3.5 mb-8">
              {POINTS.map((p, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-brand-gold to-brand-gold-dark text-white flex items-center justify-center mt-0.5 shadow-gold-glow">
                    <FlowerMotif className="w-4 h-4" />
                  </span>
                  <span className={`text-brand-text-dark text-base md:text-lg leading-relaxed ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                    {isHi ? p.hi : p.en}
                  </span>
                </li>
              ))}
            </ul>

            <PremiumButton size="lg" onClick={onOpenForm}>
              <Sparkles className="w-4 h-4" />
              <span className={isHi ? 'font-hindi' : 'font-outfit'}>
                {isHi ? 'अभी ऑर्डर करें' : 'Order Now'}
              </span>
              <ArrowRight className="w-4 h-4" />
            </PremiumButton>
          </div>

          {/* RIGHT: Premium book mockup */}
          <div className="order-1 lg:order-2 relative">
            <div className="relative max-w-md mx-auto">
              {/* Glow */}
              <div className="absolute -inset-8 bg-brand-gold/20 blur-3xl rounded-full" />

              {/* Book mockup with pages */}
              <div className="relative animate-gentle-float">
                {/* Stacked pages effect */}
                <div className="absolute -bottom-3 -right-3 w-full h-full rounded-2xl bg-brand-maroon/30 blur-md" />
                <div className="absolute -bottom-1.5 -right-1.5 w-full h-full rounded-2xl bg-brand-maroon/20" />

                {/* Main book cover */}
                <div className="relative aspect-[3/4] rounded-2xl bg-gradient-to-br from-brand-maroon via-brand-maroon-dark to-[#3a050d] overflow-hidden shadow-premium border-4 border-brand-gold">
                  {/* Mandala backdrop */}
                  <div className="absolute inset-0 bg-mandala-large bg-center bg-no-repeat opacity-40" />
                  <div
                    className="absolute inset-0 opacity-60"
                    style={{
                      background:
                        'radial-gradient(circle at 50% 30%, rgba(243,229,171,0.30) 0%, transparent 60%)',
                    }}
                  />

                  {/* Cover content */}
                  <div className="relative h-full flex flex-col items-center justify-center p-8 text-center">
                    {/* Top ornament */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-12 h-px bg-brand-gold-light/60" />
                      <FlowerMotif className="w-5 h-5 text-brand-gold-light" />
                      <span className="w-12 h-px bg-brand-gold-light/60" />
                    </div>

                    <p className="text-brand-gold-light/80 uppercase tracking-[0.4em] text-[10px] mb-2">
                      Premium
                    </p>

                    <OmMark className="w-20 h-20 text-brand-gold-light mb-3" />

                    <h3 className={`font-playfair font-bold text-3xl md:text-4xl text-brand-gold-light leading-tight mb-2 ${isHi ? 'font-hindi-serif' : ''}`}>
                      {isHi ? 'कर्मा कुंडली' : 'Karma Kundali'}
                    </h3>
                    <p className={`text-white/75 text-sm tracking-wider uppercase ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                      {isHi ? 'आपकी जन्म कुंडली' : 'Personalized Vedic Report'}
                    </p>

                    {/* Bottom ornament */}
                    <div className="flex items-center gap-2 mt-6">
                      <span className="w-12 h-px bg-brand-gold-light/60" />
                      <BookOpen className="w-4 h-4 text-brand-gold-light" />
                      <span className="w-12 h-px bg-brand-gold-light/60" />
                    </div>

                    <p className={`text-brand-gold-light/80 text-xs mt-4 ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                      {isHi ? '60+ पृष्ठ • हिंदी / English' : '60+ Pages • Hindi / English'}
                    </p>
                  </div>

                  {/* Side spine */}
                  <div className="absolute left-0 top-0 bottom-0 w-3 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
                </div>
              </div>

              {/* Floating "100% Authentic" badge */}
              <div className="absolute -top-4 -left-4 bg-brand-gold text-brand-maroon-dark rounded-full w-24 h-24 flex flex-col items-center justify-center shadow-premium border-4 border-white rotate-[-10deg]">
                <span className="font-playfair font-bold text-xl leading-none">100%</span>
                <span className={`text-[10px] uppercase tracking-wider font-bold mt-0.5 ${isHi ? 'font-hindi' : ''}`}>
                  {isHi ? 'प्रामाणिक' : 'Authentic'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatIsKundliSection;
